// Imports
import React, { useRef, useState, useContext, useEffect, useCallback } from 'react';
import { ThemeProvider } from 'styled-components/macro';
import useUpdateOnlyEffect from '../../../../../../shareables/foundation/front-end/utils/use-update-only-effect';
import saveValue from './save-value';
import FieldContainer from '../../../../../../shareables/foundation/front-end/components/forms/field-container';
import { InlineLabel } from '../../../../../../shareables/foundation/front-end/components/forms/labels';
import FieldErrorMessage from '../../../../../../shareables/foundation/front-end/components/forms/field-error-message';
import APIConnectedFormContext, { ContextDetails } from '../../../../../contexts/api-connected-form';
import debounce from 'lodash/debounce';
import { z } from 'zod';
import parsePersistentFormValue from './utils/parse-persistent-form-value';
import uniqueId from 'lodash/uniqueId';
import BaseFieldProps from './base-field-props';


// Define the accepted props
interface Props extends Omit<BaseFieldProps, 'label'> {
	/** The label of the field. */
	label: string | React.ReactElement;
	
	
	/** The initial checked state of the field. Will be ignored on subsequent renders. Defaults to `false`. */
	initiallyChecked?: boolean;
	
	
	/** A function that handles a change in value to the field. The function is passed the latest value as its parameter. */
	onChange?: (newChecked: boolean) => void;
	
	
	/** A function that provides the getter for the field to be bound, usually to a `ref`. */
	bindGetter?: (getter: () => boolean) => void;
	
	
	/** A function that provides the setter for the field to be bound, usually to a `ref`. */
	bindSetter?: (setter: (checked: boolean) => void) => void;
}

type CompleteProps = Props &
	Omit<
		React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
		| 'id'
		| 'className'
		| 'value'
		| 'defaultValue'
		| 'type'
		| 'checked'
		| 'defaultChecked'
		| 'prefix'
		| 'placeholder'
		| 'onChange'
	>;


// Function component
const Checkbox = React.forwardRef<HTMLInputElement, CompleteProps>((props: CompleteProps, ref) => {
	// Break off the props we don't want to give to the intrinsic element
	const {
		label,
		initiallyChecked,
		errorMessage: errorMessageProp,
		onChange: onChangeProp,
		bindGetter,
		bindSetter,
		recoverState: recoverStateProp,
		exclude: excludeProp,
		note,
		...passThroughProps
	} = props;
	
	
	// Use context
	const context = useContext(APIConnectedFormContext);
	
	
	// Retrieve values from API form's context, falling back to props
	const recoverState = recoverStateProp ?? context?.recoverState;
	const errorMessage = context?.error.field === passThroughProps.name ? context.error.message : errorMessageProp;
	
	
	// Decide if the field is excluded
	const exclude = excludeProp ?? passThroughProps.disabled ?? undefined;
	
	
	// Use state
	const [checked, setChecked] = useState<boolean>(Boolean(initiallyChecked));
	
	
	// Bind getter/setter
	bindGetter && bindGetter(() => checked);
	bindSetter && bindSetter(setChecked);
	
	
	// Add and remove getter from connected form
	const { name } = passThroughProps;
	let addValueGetter: NonNullable<ContextDetails>['addValueGetter'] | null = null;
	let removeValueGetter: NonNullable<ContextDetails>['removeValueGetter'] | null = null;
	
	if (context) {
		addValueGetter = context.addValueGetter;
		removeValueGetter = context.removeValueGetter;
	}
	
	useEffect(() => {
		if (addValueGetter === null || exclude === true) {
			return;
		}
		
		addValueGetter(name, () => checked);
		
		return () => {
			if (removeValueGetter === null) {
				return;
			}
			
			removeValueGetter(name);
		};
	}, [name, checked, addValueGetter, removeValueGetter, exclude]);
	
	
	// Handle value changes
	const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
		setChecked(event.target.checked);
	};
	
	
	// Debounce save value function
	const debouncedSaveValue = useCallback(debounce(saveValue, 300), []); // eslint-disable-line react-hooks/exhaustive-deps
	
	
	// Save new value on updates
	useUpdateOnlyEffect(() => {
		recoverState &&
			recoverState.enabled &&
			debouncedSaveValue(passThroughProps.name, checked, recoverState.uniqueIdentifier);
	}, [checked]);
	
	
	// Notify parent of changes
	useUpdateOnlyEffect(() => {
		onChangeProp?.(checked);
		context?.onChange?.();
	}, [checked]);
	
	useUpdateOnlyEffect(() => {
		context?.onExcludeChange?.();
	}, [exclude]);
	
	
	// Use ref
	const hasMounted = useRef(false);
	
	
	// Attempt to recover state on mount
	if (recoverState && recoverState.enabled && hasMounted.current === false) {
		hasMounted.current = true;
		
		window.setTimeout(() => {
			const recoveredChecked = parsePersistentFormValue(
				recoverState.uniqueIdentifier,
				passThroughProps.name,
				z.boolean()
			);
			
			if (typeof recoveredChecked !== 'undefined') {
				setChecked(recoveredChecked);
			}
		}, 1); // Must happen asynchronously
		
		/* Don't trigger onChange here; it will be triggered before all getters are bound, and cause issues */
	}
	
	
	// Create theme
	const theme = {
		hasError: Boolean(errorMessage),
	};
	
	
	// Generate unique ID
	const uniqueID = useRef(uniqueId('checkbox-field-'));
	
	
	// Return JSX
	return (
		<ThemeProvider theme={theme}>
			<FieldContainer>
				<InlineLabel>
					<input
						{...passThroughProps}
						type='checkbox'
						value={1}
						checked={checked}
						onChange={handleChange}
						aria-invalid={Boolean(errorMessage)}
						aria-describedby={`error-field-${uniqueID.current}`}
						ref={ref}
					/>{' '}
					<span>
						{label}
						{note && <small>{note}</small>}
					</span>
				</InlineLabel>
				
				{errorMessage && <FieldErrorMessage id={`error-field-${uniqueID.current}`} message={errorMessage} />}
			</FieldContainer>
		</ThemeProvider>
	);
});


// @ts-ignore: displayName does exist
Checkbox.displayName = 'Checkbox';

export default Checkbox;
